import { createBrowserRouter, Outlet } from 'react-router';
import {
  AnonOnly,
  RequireAdmin,
  RequireAuth,
  RequireHousehold,
  RequireUnverified,
} from './components/guards';
import { AuthProvider } from './lib/auth';
import { AdminPage } from './pages/AdminPage';
import { ConfirmEmailPage } from './pages/ConfirmEmailPage';
import { DashboardPage } from './pages/DashboardPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { LoginPage } from './pages/LoginPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { OnboardingPage } from './pages/OnboardingPage';
import { RegisterPage } from './pages/RegisterPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { PrivacyPage } from './pages/legal/PrivacyPage';
import { TermsPage } from './pages/legal/TermsPage';
import { DevicesPage } from './pages/settings/DevicesPage';
import { HouseholdPage } from './pages/settings/HouseholdPage';
import { MealPlacesPage } from './pages/settings/MealPlacesPage';
import { PasskeysPage } from './pages/settings/PasskeysPage';
import { ProfilePage } from './pages/settings/ProfilePage';
import { SchedulePage } from './pages/settings/SchedulePage';
import { SettingsPage } from './pages/settings/SettingsPage';
import { groceriesRoutes } from './pages/groceries/routes';
import { importsRoutes } from './pages/imports/routes';
import { ingredientsRoutes } from './pages/ingredients/routes';
import { pantryRoutes } from './pages/pantry/routes';
import { plansRoutes } from './pages/plans/routes';
import { recipesRoutes } from './pages/recipes/routes';
import { storesRoutes } from './pages/stores/routes';

function Root() {
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  );
}

export const router = createBrowserRouter([
  {
    element: <Root />,
    children: [
      {
        element: <AnonOnly />,
        children: [
          { path: '/login', element: <LoginPage /> },
          { path: '/register', element: <RegisterPage /> },
          { path: '/forgot-password', element: <ForgotPasswordPage /> },
          { path: '/reset-password', element: <ResetPasswordPage /> },
        ],
      },
      { path: '/verify-email', element: <VerifyEmailPage /> },
      // Signed in but not yet confirmed (D25): the only screen such a user gets.
      {
        element: <RequireUnverified />,
        children: [{ path: '/confirm-email', element: <ConfirmEmailPage /> }],
      },
      // Public, unauthenticated pages required by both app stores — must stay
      // outside AnonOnly/RequireAuth so they render for signed-out visitors.
      { path: '/privacy', element: <PrivacyPage /> },
      { path: '/terms', element: <TermsPage /> },
      {
        element: <RequireAuth />,
        children: [
          { path: '/onboarding', element: <OnboardingPage /> },
          { path: '/settings', element: <SettingsPage /> },
          { path: '/settings/profile', element: <ProfilePage /> },
          { path: '/settings/devices', element: <DevicesPage /> },
          { path: '/settings/passkeys', element: <PasskeysPage /> },
          {
            element: <RequireHousehold />,
            children: [
              { path: '/', element: <DashboardPage /> },
              { path: '/settings/household', element: <HouseholdPage /> },
              { path: '/settings/schedule', element: <SchedulePage /> },
              { path: '/settings/places', element: <MealPlacesPage /> },
              ...recipesRoutes,
              ...pantryRoutes,
              ...ingredientsRoutes,
              ...importsRoutes,
              ...plansRoutes,
              ...storesRoutes,
              ...groceriesRoutes,
            ],
          },
          {
            element: <RequireAdmin />,
            children: [{ path: '/admin', element: <AdminPage /> }],
          },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
